// Usage & Cost Dashboard (Usage & Cost Tracking, Phase 3).
// Role-aware: Super Users see usage for their assigned projects (no server/cost);
// Admins additionally see cost, server runtime, an all|assigned scope toggle, and
// the login-sessions table. Everything reads the GET /api/usage/* endpoints; role
// and project scope are enforced server-side (this component only shapes the UI).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { authHeaders } from '../../utils/authHeader';
import './UsageDashboard.css';

const PRESETS = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'year', label: 'Year' },
];

const AXIS_TICK = { fill: '#94a3b8', fontSize: 12 };
const SMALL_AXIS_TICK = { fill: '#94a3b8', fontSize: 10 };
const TOOLTIP_STYLE = {
  backgroundColor: '#09101b',
  border: '1px solid #334155',
  borderRadius: '0.5rem',
  color: '#e2e8f0',
};
const TOOLTIP_CURSOR = { fill: 'rgba(59, 130, 246, 0.08)' };
const LEGEND_STYLE = { color: '#cbd5e1' };

function fmtDuration(seconds) {
  if (seconds === null || seconds === undefined) return '—';
  const s = Math.max(0, Math.floor(seconds));
  const hh = String(Math.floor(s / 3600)).padStart(2, '0');
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(); // browser-local from UTC (doc §14.4)
  } catch {
    return iso;
  }
}

async function getJson(url) {
  const res = await fetch(url, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

const UsageDashboard = () => {
  const { permissions, loading: permsLoading } = useUserPermissions();
  const perms = permissions?.permissions || [];
  const canViewCosts = perms.includes('view_costs');
  const canViewAllProjects = perms.includes('view_all_projects');
  const canViewSessions = perms.includes('view_usage_all');
  const canViewUsage = perms.includes('view_usage_assigned') || perms.includes('view_usage_all');

  const [preset, setPreset] = useState('month');
  const [userEmail, setUserEmail] = useState('');
  const [analysisType, setAnalysisType] = useState('');
  const [status, setStatus] = useState('');
  const [project, setProject] = useState('');
  const [runId, setRunId] = useState('');
  const [scope, setScope] = useState('all'); // admin only
  const [serverType, setServerType] = useState(''); // admin only (§14.2)
  const [costMin, setCostMin] = useState('');       // admin only (§14.2)
  const [costMax, setCostMax] = useState('');       // admin only (§14.2)
  const [breakdownDim, setBreakdownDim] = useState('project');
  const [costDim, setCostDim] = useState('project');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const [options, setOptions] = useState({ analysis_types: [], server_types: [], projects: [] });
  const [summary, setSummary] = useState(null);
  const [analyses, setAnalyses] = useState({ rows: [], total: 0 });
  const [series, setSeries] = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [serverGraphs, setServerGraphs] = useState(null);
  const [costGraphs, setCostGraphs] = useState(null);
  const [sessions, setSessions] = useState({ rows: [], total: 0 });
  const [visSessions, setVisSessions] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (preset) p.set('preset', preset);
    if (userEmail.trim()) p.set('user_email', userEmail.trim());
    if (analysisType) p.set('analysis_type', analysisType);
    if (status) p.set('status', status);
    if (project) p.set('project', project);
    if (runId.trim()) p.set('run_id', runId.trim());
    if (canViewAllProjects) p.set('scope', scope);
    if (canViewCosts) {
      if (serverType.trim()) p.set('server_type', serverType.trim());
      if (costMin !== '') p.set('cost_min', costMin);
      if (costMax !== '') p.set('cost_max', costMax);
    }
    return p.toString();
  }, [preset, userEmail, analysisType, status, project, runId, scope, serverType,
      costMin, costMax, canViewAllProjects, canViewCosts]);

  const load = useCallback(async () => {
    if (!canViewUsage) return;
    setLoading(true);
    setError('');
    try {
      const requests = [
        getJson(`/api/usage/summary?${queryString}`),
        getJson(`/api/usage/analyses?${queryString}&page=${page}&page_size=${pageSize}`),
        getJson(`/api/usage/graphs/analyses-over-time?${queryString}`),
        getJson(`/api/usage/graphs/breakdown?${queryString}&dim=${breakdownDim}`),
      ];
      if (canViewCosts) {
        requests.push(getJson(`/api/usage/graphs/server-usage?${queryString}`));
        requests.push(getJson(`/api/usage/graphs/cost?${queryString}&dim=${costDim}`));
      }
      const [sum, ana, gr, bd, srv, cst] = await Promise.all(requests);
      setSummary(sum.summary || {});
      setAnalyses({ rows: ana.rows || [], total: ana.total || 0 });
      setSeries(gr.series || []);
      setBreakdown(bd.series || []);
      if (canViewCosts) {
        setServerGraphs(srv || null);
        setCostGraphs(cst || null);
      }
      // Visualization sessions are project-scoped — visible to every usage viewer.
      const vis = await getJson(`/api/usage/vis-sessions?${queryString}&page=1&page_size=${pageSize}`);
      setVisSessions({ rows: vis.rows || [], total: vis.total || 0 });
      if (canViewSessions) {
        const ses = await getJson(`/api/usage/sessions?${queryString}&page=1&page_size=${pageSize}`);
        setSessions({ rows: ses.rows || [], total: ses.total || 0 });
      }
    } catch (e) {
      setError(`Failed to load usage data: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [queryString, page, breakdownDim, costDim, canViewUsage, canViewSessions, canViewCosts]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!canViewUsage) return;
    getJson('/api/usage/filter-options')
      .then((d) => setOptions({
        analysis_types: d.analysis_types || [],
        server_types: d.server_types || [],
        projects: d.projects || [],
      }))
      .catch(() => {});
  }, [canViewUsage]);

  // Reset to page 1 when filters change.
  useEffect(() => { setPage(1); }, [queryString]);

  const downloadPdf = useCallback(async (reportType) => {
    try {
      const res = await fetch(
        `/api/usage/report.pdf?${queryString}&report_type=${reportType}`,
        { headers: await authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType}-report.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(`Report download failed: ${e.message}`);
    }
  }, [queryString]);

  if (permsLoading) {
    return (
      <div className="usage-dashboard">
        <div className="usage-content usage-state">
          <div className="usage-loading">
            <div className="usage-loading-spinner" aria-hidden="true" />
            <span>Loading…</span>
          </div>
        </div>
      </div>
    );
  }
  if (!canViewUsage) {
    return (
      <div className="usage-dashboard">
        <div className="usage-content usage-state">
          <div className="usage-denied"><h2>Access Denied</h2>
            <p>You don't have permission to view the usage dashboard.</p></div>
        </div>
      </div>
    );
  }

  const currency = summary?.currency || 'USD';
  const totalPages = Math.max(1, Math.ceil((analyses.total || 0) / pageSize));

  return (
    <div className="usage-dashboard">
      <main className="usage-content">
      <div className="usage-header">
        <h1>Usage {canViewCosts ? '& Cost ' : ''}Dashboard</h1>
        {canViewAllProjects && (
          <div className="usage-scope">
            <label>Projects:&nbsp;</label>
            <select value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="all">All Projects</option>
              <option value="assigned">Assigned Projects</option>
            </select>
          </div>
        )}
      </div>

      <div className="usage-overview-row">
        <div className="usage-tiles">
          <Tile label="Analyses" value={summary?.analyses_total ?? '—'} />
          <Tile label="Successful" value={summary?.analyses_successful ?? '—'} />
          <Tile label="Unsuccessful" value={summary?.analyses_unsuccessful ?? '—'} />
          {canViewSessions && <Tile label="Login sessions" value={sessions.total ?? '—'} />}
          {canViewCosts && (
            <Tile label="Server runtime"
              value={fmtDuration(summary?.total_server_runtime_seconds)} />
          )}
          {canViewCosts && (
            <Tile label="Total cost"
              value={summary?.total_cost_status === 'not_configured'
                ? 'Cost not configured'
                : (summary?.total_cost != null ? `${currency} ${summary.total_cost}` : '—')} />
          )}
        </div>

        <div className="usage-filters">
          <div className="preset-group">
            {PRESETS.map((p) => (
              <button key={p.key} type="button"
                className={`usage-filter-preset ${preset === p.key ? 'active' : ''}`}
                onClick={() => setPreset(p.key)}>{p.label}</button>
            ))}
          </div>
          <input type="text" className="usage-filter-control usage-filter-input"
            placeholder="User email"
            value={userEmail} onChange={(e) => setUserEmail(e.target.value)} />
          <select className="usage-filter-control usage-filter-select"
            value={project} onChange={(e) => setProject(e.target.value)}>
            <option value="">All projects</option>
            {options.projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <input type="text" className="usage-filter-control usage-filter-input"
            placeholder="Run ID"
            value={runId} onChange={(e) => setRunId(e.target.value)} />
          <select className="usage-filter-control usage-filter-select"
            value={analysisType} onChange={(e) => setAnalysisType(e.target.value)}>
            <option value="">All types</option>
            {options.analysis_types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select className="usage-filter-control usage-filter-select"
            value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Any status</option>
            <option value="successful">Successful</option>
            <option value="unsuccessful">Unsuccessful</option>
            <option value="running">Running</option>
          </select>
          {canViewCosts && (
            <>
              <select className="usage-filter-control usage-filter-select usage-narrow"
                value={serverType} onChange={(e) => setServerType(e.target.value)}>
                <option value="">Any server</option>
                {options.server_types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input type="number" placeholder="Cost ≥"
                className="usage-filter-control usage-filter-input usage-narrow" min="0" step="0.01"
                value={costMin} onChange={(e) => setCostMin(e.target.value)} />
              <input type="number" placeholder="Cost ≤"
                className="usage-filter-control usage-filter-input usage-narrow" min="0" step="0.01"
                value={costMax} onChange={(e) => setCostMax(e.target.value)} />
            </>
          )}
          <button type="button" className="usage-filter-action usage-filter-refresh"
            onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
          <button type="button" className="usage-filter-action usage-filter-pdf"
            onClick={() => downloadPdf('usage')}>Usage PDF</button>
          {canViewCosts && (
            <button type="button" className="usage-filter-action usage-filter-pdf"
              onClick={() => downloadPdf('cost')}>Cost PDF</button>
          )}
        </div>
      </div>

      {error && <div className="usage-error">{error}</div>}

      <div className="usage-chart-grid">
        <div className="usage-chart">
          <h3>Analyses over time</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={series} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--usage-grid, #e2e8f0)" />
              <XAxis dataKey="date" tick={AXIS_TICK} />
              <YAxis allowDecimals={false} tick={AXIS_TICK} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={TOOLTIP_CURSOR} />
              <Legend wrapperStyle={LEGEND_STYLE} />
              <Line type="monotone" dataKey="successful" stroke="#2563eb" name="Successful" dot={false} />
              <Line type="monotone" dataKey="unsuccessful" stroke="#dc2626" name="Unsuccessful" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="usage-chart">
          <h3 className="usage-chart-select-title">
            <span>Analyses by</span>
            <select className="usage-chart-title-select"
              value={breakdownDim} onChange={(e) => setBreakdownDim(e.target.value)}>
              <option value="project">project</option>
              <option value="run">run</option>
              <option value="user">user</option>
              <option value="type">analysis type</option>
            </select>
          </h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={breakdown} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--usage-grid, #e2e8f0)" />
              <XAxis dataKey="key" tick={SMALL_AXIS_TICK} interval={0} angle={-25} textAnchor="end" height={60} />
              <YAxis allowDecimals={false} tick={AXIS_TICK} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={TOOLTIP_CURSOR} />
              <Legend wrapperStyle={LEGEND_STYLE} />
              <Bar dataKey="successful" stackId="a" fill="#2563eb" name="Successful" />
              <Bar dataKey="unsuccessful" stackId="a" fill="#dc2626" name="Unsuccessful" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {canViewCosts && serverGraphs && (
          <div className="usage-chart">
            <h3>Server usage by type (hours)</h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart
                data={(serverGraphs.by_server_type || []).map((r) => ({
                  key: r.key, hours: Math.round((r.duration_seconds / 3600) * 100) / 100,
                }))}
                margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--usage-grid, #e2e8f0)" />
                <XAxis dataKey="key" tick={SMALL_AXIS_TICK} interval={0} angle={-25} textAnchor="end" height={60} />
                <YAxis tick={AXIS_TICK} />
                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={TOOLTIP_CURSOR} />
                <Bar dataKey="hours" fill="#0f766e" name="Hours" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {canViewCosts && costGraphs && (
          <div className="usage-chart">
            <h3>Cost over time ({costGraphs.currency || 'USD'})</h3>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={costGraphs.over_time || []} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--usage-grid, #e2e8f0)" />
                <XAxis dataKey="date" tick={AXIS_TICK} />
                <YAxis tick={AXIS_TICK} />
                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={TOOLTIP_CURSOR} />
                <Line type="monotone" dataKey="cost" stroke="#7c3aed" name="Cost" dot={false} />
              </LineChart>
            </ResponsiveContainer>
            {costGraphs.unrated_seconds > 0 && (
              <p className="usage-muted usage-unrated">
                {fmtDuration(costGraphs.unrated_seconds)} of server time has no configured
                rate and is excluded (Cost not configured).
              </p>
            )}
          </div>
        )}

        {canViewCosts && costGraphs && (
          <div className="usage-chart">
            <h3 className="usage-chart-select-title">
              <span>Cost by</span>
              <select className="usage-chart-title-select"
                value={costDim} onChange={(e) => setCostDim(e.target.value)}>
                <option value="project">project</option>
                <option value="user">user</option>
                <option value="type">analysis type</option>
                <option value="server_type">server type</option>
              </select>
              <span>({costGraphs.currency || 'USD'})</span>
            </h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={costGraphs.by_dim || []} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--usage-grid, #e2e8f0)" />
                <XAxis dataKey="key" tick={SMALL_AXIS_TICK} interval={0} angle={-25} textAnchor="end" height={60} />
                <YAxis tick={AXIS_TICK} />
                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={TOOLTIP_CURSOR} />
                <Bar dataKey="cost" fill="#7c3aed" name="Cost" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="usage-table-wrap">
        <h3>Analyses</h3>
        <table className="usage-table">
          <thead>
            <tr>
              <th>Date</th><th>User</th><th>Project</th><th>Run</th><th>Type</th>
              <th>Start</th><th>Finish</th><th>Duration</th><th>Status</th>
              {canViewCosts && <><th>Servers</th><th>Cost</th></>}
            </tr>
          </thead>
          <tbody>
            {analyses.rows.map((r) => (
              <tr key={`${r.run_id}-${r.analysis_type}`}>
                <td>{fmtDateTime(r.start_time).split(',')[0]}</td>
                <td>{r.user_email || '—'}</td>
                <td>{r.project_name || r.project_id || 'Deleted project'}</td>
                <td>
                  <button type="button" className="usage-runlink" title={r.run_id}
                    onClick={() => setRunId(r.run_id)}>
                    {shortRunId(r.run_id)}
                  </button>
                </td>
                <td>{r.analysis_type}</td>
                <td>{fmtDateTime(r.start_time)}</td>
                <td>{fmtDateTime(r.finish_time)}</td>
                <td>{fmtDuration(r.duration_seconds)}</td>
                <td><StatusBadge row={r} /></td>
                {canViewCosts && <td>{renderServers(r)}</td>}
                {canViewCosts && <td>{renderCost(r, currency)}</td>}
              </tr>
            ))}
            {analyses.rows.length === 0 && !loading && (
              <tr><td colSpan={canViewCosts ? 11 : 9} className="usage-empty">No analyses match the filters.</td></tr>
            )}
          </tbody>
        </table>
        <div className="usage-pager">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
          <span>Page {page} of {totalPages} ({analyses.total} total)</span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      </div>

      <div className="usage-table-wrap">
        <h3>Visualization sessions</h3>
        <table className="usage-table">
          <thead>
            <tr><th>User</th><th>Project</th><th>Source</th><th>Start</th><th>End</th><th>Duration</th></tr>
          </thead>
          <tbody>
            {visSessions.rows.map((s, i) => (
              <tr key={i}>
                <td>{s.user_email}</td>
                <td>{projectLabel(s.project_id, options.projects)}</td>
                <td>{s.source || '—'}</td>
                <td>{fmtDateTime(s.login_time)}</td>
                <td>{s.active ? <em>active</em> : fmtDateTime(s.logout_time)}</td>
                <td>{s.active ? '—' : fmtDuration(s.duration_seconds)}</td>
              </tr>
            ))}
            {visSessions.rows.length === 0 && !loading && (
              <tr><td colSpan={6} className="usage-empty">No visualization sessions match the filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {canViewSessions && (
        <div className="usage-table-wrap">
          <h3>Login sessions</h3>
          <table className="usage-table">
            <thead>
              <tr><th>User</th><th>Login</th><th>Logout</th><th>Duration</th><th>Reason</th></tr>
            </thead>
            <tbody>
              {sessions.rows.map((s, i) => (
                <tr key={i}>
                  <td>{s.user_email}</td>
                  <td>{fmtDateTime(s.login_time)}</td>
                  <td>{s.active ? <em>active</em> : fmtDateTime(s.logout_time)}</td>
                  <td>{s.active ? '—' : fmtDuration(s.login_duration_seconds)}</td>
                  <td>{s.logout_reason || '—'}</td>
                </tr>
              ))}
              {sessions.rows.length === 0 && !loading && (
                <tr><td colSpan={5} className="usage-empty">No sessions match the filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      </main>
    </div>
  );
};

const Tile = ({ label, value }) => (
  <div className="usage-tile"><div className="tile-value">{value}</div><div className="tile-label">{label}</div></div>
);

const StatusBadge = ({ row }) => {
  const s = row.status;
  const cls = s === 'successful' ? 'ok' : s === 'unsuccessful' ? 'bad' : 'run';
  return (
    <span className={`usage-status ${cls}`}>
      {s}
      {s === 'unsuccessful' && (row.log_ref
        ? <span className="usage-loghint" title={`log: ${row.log_ref.run_id || ''}`}> ⓘ</span>
        : <span className="usage-loghint" title="Log file not available"> —</span>)}
    </span>
  );
};

// "project_2442_run_pyexisting_280726-0341-2b6e" → "280726-0341-2b6e"
function shortRunId(runId) {
  if (!runId) return '—';
  const m = String(runId).match(/run_(?:\w+_)?(.+)$/);
  return m ? m[1] : runId;
}

function projectLabel(projectId, projects) {
  if (projectId == null) return '—';
  const hit = (projects || []).find((p) => p.id === String(projectId));
  return hit ? hit.name : `Project ${projectId}`;
}

function renderServers(r) {
  if (r.cost_status === 'not_available' || !r.servers || r.servers.length === 0) {
    return <span className="usage-muted">Server usage not available</span>;
  }
  return r.servers.map((s, i) => (
    <div key={i}>{s.server_type}: {fmtDuration(s.duration_seconds)}</div>
  ));
}

function renderCost(r, currency) {
  if (r.cost_status === 'not_available') return <span className="usage-muted">—</span>;
  if (r.cost_status === 'not_configured') return <span className="usage-muted">Cost not configured</span>;
  if (r.total_cost == null) return '—';
  return `${currency} ${r.total_cost}`;
}

export default UsageDashboard;
